import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Award, Users, Lock, Star, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, getChildId } from "../../lib/api";
import { loadRecordings, play, setAudioPrefs } from "../../lib/audio";
import { ParentGate } from "../../components/ParentGate";
import { Avatar } from "../../components/Avatar";
import { usePremiumPrompt, CrownBadge } from "../../components/billing/PremiumPrompt";

const TopicCard = ({ t, i, onTap }) => {
  const locked = t.state === "locked" || t.state === "premium";
  return (
    <motion.button data-testid={`speak-topic-${t.topic_id}`} onClick={() => onTap(t)} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      whileHover={locked ? {} : { y: -6 }} whileTap={{ scale: 0.97 }}
      className={`text-left rounded-[2rem] overflow-hidden border-4 bg-white shadow-md flex flex-col ${locked ? "border-stone-200" : ""}`} style={locked ? {} : { borderColor: `${t.color}66` }}>
      <div className="relative h-40 overflow-hidden">
        <img src={t.cover_image} alt="" className={`w-full h-full object-cover ${locked ? "grayscale opacity-60" : ""}`} />
        <span className="absolute top-3 left-3 w-12 h-12 rounded-full text-white text-2xl flex items-center justify-center shadow" style={{ background: locked ? "#a8a29e" : t.color }}>
          {locked ? <Lock size={20} /> : i + 1}
        </span>
        <span className="absolute bottom-3 right-3 text-4xl drop-shadow">{t.state === "premium" ? <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center" data-testid={`speak-topic-premium-${t.topic_id}`}><CrownBadge size={26} /></span> : t.emoji}</span>
      </div>
      <div className="p-4 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className={`text-2xl sm:text-3xl truncate ${locked ? "text-stone-400" : "text-stone-800"}`}>{t.title}</div>
          <div className="text-base text-stone-500">{t.sentence_count} 句</div>
        </div>
        {t.state === "completed" && (
          <span className="flex" data-testid={`speak-topic-stars-${t.topic_id}`}>{[1, 2, 3].map((k) => <Star key={k} size={20} className={k <= t.stars ? "text-amber-400" : "text-stone-200"} fill="currentColor" />)}</span>
        )}
      </div>
    </motion.button>
  );
};

export default function SpeakHall() {
  const nav = useNavigate();
  const childId = getChildId();
  const [data, setData] = useState(null);
  const [gate, setGate] = useState(false);
  useEffect(() => {
    if (!childId) return nav("/profiles");
    loadRecordings();
    api.get(`/children/${childId}/speak`).then(({ data }) => { setData(data); setAudioPrefs(data.child.settings); }).catch(() => nav("/profiles"));
  }, [childId, nav]);
  const [prompt, premiumEl] = usePremiumPrompt();
  const tap = (t) => {
    if (t.state === "premium") return prompt();
    if (t.state === "locked") { play("先完成上一課喔"); toast("先完成上一課，就能打開這一課喔！"); return; }
    play(t.title.replace(/[…]+/g, ""));
    nav(`/speak/${t.topic_id}`);
  };
  if (!data) return <div className="min-h-screen speak-bg" />;
  const { child, topics } = data;
  const done = topics.filter((t) => t.state === "completed").length;
  return (
    <div className="min-h-screen speak-bg font-round" data-testid="speak-hall-page">
      <header className="sticky top-0 z-10 flex items-center gap-2 sm:gap-3 px-3 sm:px-8 py-3 bg-white/80 backdrop-blur-md border-b-2 border-[var(--sp-soft-b)]">
        <button data-testid="speak-back-btn" onClick={() => nav("/halls")} className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-white border-2 border-[var(--sp-soft-b)] flex items-center justify-center"><ArrowLeft /></button>
        <span className="text-4xl w-14 h-14 shrink-0 rounded-full bg-[var(--sp-soft)] border-4 border-[var(--sp-soft-b)] hidden sm:flex items-center justify-center overflow-hidden"><Avatar value={child.avatar} /></span>
        <div className="text-2xl sm:text-3xl text-[var(--sp-deep)] flex items-center gap-2"><MessageCircle className="text-[var(--sp)]" /> 我會說中文館</div>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
          <div className="hidden sm:flex items-center gap-2 px-4 h-14 rounded-full bg-white border-2 border-amber-300 text-2xl text-amber-600" data-testid="speak-star-count"><Star fill="currentColor" /> {child.stars}</div>
          <button data-testid="speak-rewards-btn" onClick={() => nav("/rewards")} className="w-14 h-14 rounded-full bg-[var(--sp)] text-white flex items-center justify-center shadow-md"><Award size={28} /></button>
          <button data-testid="speak-parent-btn" onClick={() => setGate(true)} className="w-14 h-14 rounded-full bg-white border-2 border-stone-300 text-stone-500 flex items-center justify-center"><Users size={22} /></button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 pb-16">
        <h1 className="text-4xl sm:text-5xl text-[var(--sp-deep)] mb-2">今天想說什麼呢？</h1>
        <p className="text-xl text-stone-500 mb-8" data-testid="speak-hall-progress">每一課練習 {topics[0]?.sentence_count || 10} 句生活中文，說完一課就能打開下一課。已完成 {done} / {topics.length} 課</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {topics.map((t, i) => <TopicCard key={t.topic_id} t={t} i={i} onTap={tap} />)}
        </div>
      </main>
      <ParentGate open={gate} onOpenChange={setGate} onPass={() => nav("/parent")} />
      {premiumEl}
    </div>
  );
}
